import React, { FC, useState } from 'react';
import styled, { css } from 'styled-components';
import { Link } from 'gatsby';
import { Button, ButtonProps } from './Button';
import { Panel } from './Panel';
import { Typography } from './Typography';
import { CtaCardFragment } from '../types/graphqlTypes';
import { ButtonRenderer } from './buttonRenderer/ButtonRenderer';
import { createMarkup } from '../utils';
import { ThemeButtonKeys } from '../styles/tokens/buttons';
import { Spacer } from './Spacer';
import { TickList } from './TickList';
import { Img } from './Img';
import { ThemeColorsKeys } from '../styles/tokens/colors';

interface CTACardProps extends CtaCardFragment {
  shadow?: boolean;
  inlineButton?: boolean;
  customButton?: ButtonProps;
  tightSpacing?: boolean;
  tickLisItems?: Array<{
    items: string;
  }>;
  linkItems?: Array<{ text: string; url: string; state: object }>;
  headingComponent?: 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

interface StyledCTACardProps {
  inlineButton?: boolean;
  tightSpacing?: boolean;
  hasImage?: boolean;
}

const StyledBody = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media (max-width: ${maxMedia.sm}) {
      & > * {
        /* margin-bottom: ${space.lg}; */
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  `};
`;

const StyledImage = styled(Img)`
  ${({ theme: { space, maxMedia } }) => css`
    flex-shrink: 0;
    @media (max-width: ${maxMedia.sm}) {
      margin-bottom: ${space.lg};
    }
  `};
`;

const StyledLead = styled(Typography)`
  ${({ theme: { space, media } }) => css`
    /* margin-bottom: ${space.lg}; */

    @media (min-width: ${media.md}) {
      max-width: 92%;
    }
  `};
`;

const StyledTickLists = styled(TickList)`
  ${({ theme: { space } }) => css`
    margin-top: ${space.lg};
  `};
`;

const StyledLinkList = styled.div`
  ${({ theme: { space, maxMedia } }) => css`
    margin-top: ${space.md};
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: ${space.md};

    @media (max-width: ${maxMedia.lg}) {
      justify-content: flex-start;
      flex-direction: column;
      gap: ${space.sm};
      margin-top: 24px;
    }
  `}
`;

const StyledLink = styled(Link)`
  ${({ theme: { colors, space, media, maxMedia } }) => css`
    color: ${colors.white};
    text-decoration: none;
    padding-right: ${space.lg};

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: 650px) {
      padding-right: 0;
    }

    @media (min-width: ${media.md}) {
      display: flex;
      align-items: center;
      gap: ${space.xs};
      padding-right: 0;
    }

    svg {
      width: ${space.md};
      height: ${space.md};

      @media (max-width: ${maxMedia.md}) {
        display: inline;
        top: 3px;
        position: relative;
      }
    }
  `}
`;

const StyledSeparator = styled.span`
  ${({ theme: { media } }) => css`
    display: none;

    @media (min-width: ${media.lg}) {
      display: inline;
    }
  `}
`;

const StyledButtons = styled.div`
  ${({ theme: { space, media, maxMedia } }) => css`
    display: flex;
    gap: ${space.sm};
    flex-shrink: 0;
    margin-top: ${space.lg};

    @media (max-width: ${maxMedia.md}) {
      > * {
        width: 100%;
      }
    }

    @media (max-width: ${maxMedia.md}) {
      flex-direction: column;
    }

    @media (min-width: ${media.xl}) {
      justify-content: space-between;
      align-items: center;
    }
  `};
`;

const StyledCTACard = styled(Panel)<StyledCTACardProps>`
  ${({ theme: { space, media }, inlineButton, tightSpacing, hasImage }) => css`
    a,
    button {
      align-self: flex-start;
    }

    ${hasImage &&
    css`
      @media (min-width: ${media.sm}) {
        > div {
          display: flex;
          flex-direction: row-reverse;
          align-items: center;
          gap: ${space.xl};
        }
      }
    `};

    // Inline button styles

    ${inlineButton &&
    css`
      // Desktop > styles

      @media (min-width: ${media.xl}) {
        a,
        button {
          align-self: unset;
          flex-shrink: 0;
        }

        ${StyledButtons} {
          margin: 0;
        }

        ${StyledLead} {
          margin-bottom: 0;
          max-width: none;
        }

        ${StyledBody} {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: ${tightSpacing ? 'center' : 'flex-end'};
          gap: ${space.lg};
        }
      }
    `};
  `};
`;

export const CTACard: FC<Omit<CTACardProps, '__typename'>> = ({
  title,
  lead,
  backgroundColour,
  shadow,
  button,
  tickLisItems,
  secondaryButton,
  inlineButton,
  customButton,
  tightSpacing,
  image,
  headingComponent,
  linkItems,
  ...rest
}) => {
  // Leaving content as state like this in case another CRO test needs to overide the content
  const [content, setContent] = useState<CTACardProps>({
    title,
    lead,
    button,
    customButton,
  });
  // Button variant
  let buttonVariant = 'solid' as ThemeButtonKeys;
  let secondaryButtonVariant = 'outline' as ThemeButtonKeys;

  if (backgroundColour === 'grey1' || backgroundColour === 'grey2') {
    buttonVariant = 'outline';
    secondaryButtonVariant = 'solid';
  }

  if (backgroundColour === 'brand') {
    buttonVariant = 'outlineReversed';
    secondaryButtonVariant = 'solidReversed';
  }

  const hasButtons = Boolean(secondaryButton || button || customButton);

  return (
    <StyledCTACard
      backgroundColor={(backgroundColour as ThemeColorsKeys) || 'white'}
      shadow={shadow}
      inlineButton={inlineButton}
      tightSpacing={tightSpacing}
      hasImage={!!image}
      {...rest}
    >
      {image && <StyledImage src={image} />}
      <StyledBody>
        <Spacer spacing={tightSpacing ? 'quarter' : 'half'}>
          {content.title && (
            <Typography component={headingComponent || 'h3'} variant="titleMedium">
              {content.title}
            </Typography>
          )}
          {content.lead && (
            <StyledLead component="p" variant="lead">
              {createMarkup(content.lead)}
            </StyledLead>
          )}
        </Spacer>
        {tickLisItems && <StyledTickLists items={tickLisItems} color="white" />}
        {linkItems && (
          <StyledLinkList>
            {linkItems.map((item, index) => (
              <React.Fragment key={item.text}>
                <StyledLink to={item.url} state={item.state ? item.state : undefined}>
                  {item.text}
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 16">
                    <path
                      fill="#fff"
                      fillRule="evenodd"
                      d="M6.75 12.1676V4.12624c0-.55681.67322-.83565 1.06694-.44193L11.8376 7.705c.2441.24406.2441.63981 0 .88387L7.81694 12.6096c-.39372.3937-1.06694.1148-1.06694-.442Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </StyledLink>
                {index < linkItems.length - 1 && <StyledSeparator>|</StyledSeparator>}
              </React.Fragment>
            ))}
          </StyledLinkList>
        )}

        {hasButtons && (
          <StyledButtons>
            {secondaryButton && <ButtonRenderer button={secondaryButton} variant={secondaryButtonVariant} />}
            {button && content.button && <ButtonRenderer button={content.button} variant={buttonVariant} />}
            {customButton && content.customButton && <Button {...content.customButton} variant={buttonVariant} />}
          </StyledButtons>
        )}
      </StyledBody>
    </StyledCTACard>
  );
};

export default CTACard;
